import backendV2Service from './backend-v2.service';

/**
 * Academic Period interface
 * Represents an academic period (periodo lectivo) entity from the backend
 * Note: Backend returns snake_case but humps transforms it to camelCase
 */
export interface AcademicPeriod {
  id: number;
  name: string;
  startDate: string;
  endDate: string;
  campusId: number;
  calendarTypeId: number | null; // null for custom periods
  createdAt: string;
  updatedAt: string;
}

/**
 * Academic Period Create/Update DTO
 */
export interface AcademicPeriodDto {
  name: string;
  startDate: string;
  endDate: string;
  campusId: number;
  calendarTypeId?: number | null;
}

/**
 * Academic Period Service
 * Handles all academic period related API calls using Backend V2
 */
export default {
  /**
   * Get all academic periods for a specific school
   * @param schoolId - School ID (mapped to campus_id in backend)
   * @returns Promise with array of academic periods
   */
  findAllBySchool: (schoolId: number) => backendV2Service.get<AcademicPeriod[]>(`/academic-periods?campus_id=${schoolId}`),

  /**
   * Get all academic periods
   * @returns Promise with array of academic periods
   */
  findAll: () => backendV2Service.get<AcademicPeriod[]>('/academic-periods'),

  /**
   * Get a single academic period by ID
   * @param id - Academic period ID
   * @returns Promise with academic period data
   */
  findOne: (id: number) => backendV2Service.get<AcademicPeriod>(`/academic-periods/${id}`),

  /**
   * Create a new academic period
   * @param data - Academic period data
   * @returns Promise with created academic period
   */
  create: (data: AcademicPeriodDto) => backendV2Service.post<AcademicPeriod>('/academic-periods', data),

  /**
   * Update an existing academic period
   * @param id - Academic period ID
   * @param data - Academic period data to update
   * @returns Promise with updated academic period
   */
  update: (id: number, data: Partial<AcademicPeriodDto>) => backendV2Service.put<AcademicPeriod>(`/academic-periods/${id}`, data),

  /**
   * Delete an academic period
   * @param id - Academic period ID
   * @returns Promise
   */
  delete: (id: number) => backendV2Service.delete(`/academic-periods/${id}`),
};
